import { useCallback } from 'react';

import { RenewalCalendarEditPayload } from '@/modules/Renewals/renewals.types';
import usePayrollCalendarsStore from '@/modules/Renewals/RenewalSetup/stores/usePayrollCalendars.store';

import {
  useEditablePayrollCalendarStore,
  usePayrollCalendarModalStore,
} from '../RenewalPayrollCalendarModal/stores';
import { adaptRenewalCalendarToEditable } from '../utils/renewalPayrollCalendarAdapter';

export const useRenewalPayrollCalendarEdit = () => {
  const payrollCalendars = usePayrollCalendarsStore((state) => state.state.payrollCalendars);
  const setPayrollCalendarsState = usePayrollCalendarsStore((state) => state.setState);

  const handleSetEditMode = usePayrollCalendarModalStore((state) => state.handleSetEditMode);
  const handleSetEditableId = usePayrollCalendarModalStore((state) => state.handleSetEditableId);
  const handleChangeVisibility = usePayrollCalendarModalStore((state) => state.handleChangeVisibility);
  const handleSetEditableCalendar = useEditablePayrollCalendarStore(
    (state) => state.handleSetEditableCalendar
  );

  const handleEditCalendar = useCallback(
    (calendarId: string) => {
      const calendar = (payrollCalendars || []).find((item) => item.id === calendarId);
      if (!calendar) return;

      handleSetEditableCalendar(adaptRenewalCalendarToEditable(calendar));
      handleSetEditMode(true);
      handleSetEditableId(calendarId);
      handleChangeVisibility(true);
    },
    [
      payrollCalendars,
      handleSetEditableCalendar,
      handleSetEditMode,
      handleSetEditableId,
      handleChangeVisibility,
    ]
  );

  /**
   * Called when the user saves changes in the modal (step 2 Save button).
   * Only updates the four user-editable fields; rawDto and everything else stays intact.
   */
  const handleCalendarReady = useCallback(
    (payload: RenewalCalendarEditPayload, editableId?: string) => {
      if (!editableId) return;

      setPayrollCalendarsState((prev) => ({
        ...prev,
        payrollCalendars: (prev.payrollCalendars || []).map((calendar) =>
          calendar.id === editableId
            ? {
                ...calendar,
                name: payload.name ?? calendar.name,
                frequencyType: payload.config?.frequency ?? calendar.frequencyType,
                config: payload.config ?? calendar.config,
                schedule: payload.schedule ?? calendar.schedule,
              }
            : calendar
        ),
      }));
    },
    [setPayrollCalendarsState]
  );

  return {
    handleEditCalendar,
    handleCalendarReady,
  };
};

export default useRenewalPayrollCalendarEdit;
